import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, getErrorMessage } from "@/lib/queryClient";
import { apiUrl } from "@/lib/api";
import { formatDate, formatTime } from "@/lib/date-format";
import { useParams, useLocation, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Archive,
  Monitor,
  MapPin,
  Clock,
  CreditCard,
  Calendar,
  AlertCircle,
  CheckCircle,
  Plus,
} from "lucide-react";
import type { Student } from "@contracts/student";
import type { ScheduleRule } from "@contracts/schedule";
import type { Lesson } from "@contracts/lesson";
import StudentFormDialog from "@/components/student-form-dialog";
import LessonFormDialog from "@/components/lesson-form-dialog";
import ScheduleEventDialog from "@/components/schedule-event-dialog";
import ConfirmDialog from "@/components/confirm-dialog";
import UndoDeleteBanner from "@/components/undo-delete-banner";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function StudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [showScheduleEvent, setShowScheduleEvent] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteMode, setDeleteMode] = useState<"archive" | "permanent" | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lessonTab, setLessonTab] = useState<"unpaid" | "paid" | "all">("unpaid");
  const [selectedLessonIds, setSelectedLessonIds] = useState<Set<string>>(() => new Set());
  const [lessonPendingDelete, setLessonPendingDelete] = useState<Lesson | null>(null);
  const [showBulkLessonDeleteConfirm, setShowBulkLessonDeleteConfirm] = useState(false);
  const pendingDeleteRef = useRef<{ timer: ReturnType<typeof setTimeout> } | null>(null);
  const [pendingDeleteUi, setPendingDeleteUi] = useState<{
    message: string;
    deadline: number;
    durationMs: number;
  } | null>(null);

  const { data: student, isLoading: studentLoading } = useQuery<Student>({
    queryKey: ["/api/students", id],
  });

  const { data: lessons = [], isLoading: lessonsLoading } = useQuery<Lesson[]>({
    queryKey: ["/api/lessons", { studentId: id }],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/lessons?studentId=${id}`), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const { data: debt } = useQuery<{ totalDebt: number; currency: string; unpaidLessons: Lesson[] }>({
    queryKey: ["/api/students", id, "debt"],
  });

  const { data: canDeleteInfo } = useQuery<{ canDelete: boolean; unpaidPastLessons: number }>({
    queryKey: ["/api/students", id, "can-delete"],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/students/${id}/can-delete`), { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/students/${id}/archive`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      navigate("/students");
      toast({ title: "Student archived", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Failed to archive student"), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/students/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      navigate("/students");
      toast({ title: "Student permanently deleted", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Failed to delete student"), variant: "destructive" });
    },
  });

  function handleDeleteChoice(mode: "archive" | "permanent") {
    setDeleteMode(mode);
    setShowDeleteDialog(false);
    setShowDeleteConfirm(true);
  }

  useEffect(() => {
    return () => {
      if (pendingDeleteRef.current) {
        clearTimeout(pendingDeleteRef.current.timer);
        pendingDeleteRef.current = null;
      }
      setPendingDeleteUi(null);
    };
  }, []);

  function cancelPendingDelete() {
    if (!pendingDeleteRef.current) return;
    clearTimeout(pendingDeleteRef.current.timer);
    pendingDeleteRef.current = null;
    setPendingDeleteUi(null);
  }

  function queuePendingDeleteAction(action: () => void, message: string) {
    if (pendingDeleteRef.current) {
      clearTimeout(pendingDeleteRef.current.timer);
      pendingDeleteRef.current = null;
    }
    const timer = setTimeout(() => {
      pendingDeleteRef.current = null;
      setPendingDeleteUi(null);
      action();
    }, 10_000);
    pendingDeleteRef.current = { timer };
    setPendingDeleteUi({
      message,
      deadline: Date.now() + 10_000,
      durationMs: 10_000,
    });
  }

  function handleConfirmDelete() {
    const mode = deleteMode;
    queuePendingDeleteAction(
      () => {
        if (mode === "archive") archiveMutation.mutate();
        else deleteMutation.mutate();
      },
      mode === "archive" ? "Archiving student in 10 seconds" : "Deleting student in 10 seconds"
    );
    setShowDeleteConfirm(false);
  }

  function queueLessonDelete(lessonId: string) {
    queuePendingDeleteAction(
      () => deleteLessonMutation.mutate(lessonId),
      "Deleting lesson in 10 seconds"
    );
    setLessonPendingDelete(null);
  }

  function queueBulkLessonDelete(lessonIds: string[]) {
    queuePendingDeleteAction(
      () => bulkDeleteLessonsMutation.mutate(lessonIds),
      `Deleting ${lessonIds.length} selected lesson${lessonIds.length !== 1 ? "s" : ""} in 10 seconds`
    );
    setShowBulkLessonDeleteConfirm(false);
  }

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ lessonId, status }: { lessonId: string; status: string }) => {
      const res = await apiRequest("PATCH", `/api/lessons/${lessonId}/status`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students", id, "debt"] });
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async (body: { lessonIds: string[]; status: "paid" | "pending" }) => {
      const res = await apiRequest("PATCH", "/api/lessons/bulk/status", body);
      return res.json() as Promise<{ updated: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students", id, "debt"] });
      setSelectedLessonIds(new Set());
      toast({
        title: `Updated ${data.updated} lesson${data.updated !== 1 ? "s" : ""}`,
        variant: "success",
      });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Bulk update failed"), variant: "destructive" });
    },
  });

  const deleteLessonMutation = useMutation({
    mutationFn: async (lessonId: string) => {
      await apiRequest("DELETE", `/api/lessons/${lessonId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students", id, "debt"] });
      toast({ title: "Lesson deleted", variant: "success" });
      setLessonPendingDelete(null);
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Could not delete lesson"), variant: "destructive" });
    },
  });

  const bulkDeleteLessonsMutation = useMutation({
    mutationFn: async (lessonIds: string[]) => {
      await Promise.all(lessonIds.map((lessonId) => apiRequest("DELETE", `/api/lessons/${lessonId}`)));
      return lessonIds.length;
    },
    onSuccess: (deletedCount) => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students", id, "debt"] });
      setSelectedLessonIds(new Set());
      setShowBulkLessonDeleteConfirm(false);
      toast({
        title: `Deleted ${deletedCount} lesson${deletedCount !== 1 ? "s" : ""}`,
        variant: "success",
      });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Bulk lesson delete failed"), variant: "destructive" });
    },
  });

  useEffect(() => {
    setSelectedLessonIds(new Set());
  }, [lessonTab]);

  const unpaidLessons = useMemo(
    () => lessons.filter((l) => l.paymentStatus === "pending"),
    [lessons]
  );
  const paidLessonsList = useMemo(
    () => lessons.filter((l) => l.paymentStatus === "paid"),
    [lessons]
  );

  function toggleLessonSelect(lessonId: string) {
    setSelectedLessonIds((prev) => {
      const n = new Set(prev);
      if (n.has(lessonId)) n.delete(lessonId);
      else n.add(lessonId);
      return n;
    });
  }

  if (studentLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="p-6 text-center py-20">
        <h2 className="text-xl font-semibold mb-2">Student not found</h2>
        <Button asChild variant="outline">
          <Link href="/students">Back to Students</Link>
        </Button>
      </div>
    );
  }

  const scheduleRules = (student.scheduleRules as ScheduleRule[]) || [];

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 max-w-5xl mx-auto" data-testid="page-student-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild data-testid="button-back">
          <Link href="/students">
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate" data-testid="text-student-name">{student.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge variant={student.lessonType === "online" ? "default" : "secondary"}>
              {student.lessonType === "online" ? (
                <><Monitor className="w-3 h-3 mr-1" />Online</>
              ) : (
                <><MapPin className="w-3 h-3 mr-1" />Offline</>
              )}
            </Badge>
            <Badge variant="outline">{student.gender}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StudentFormDialog
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            student={student}
            trigger={
              <Button variant="outline" size="sm" data-testid="button-edit-student">
                <Edit className="w-3.5 h-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            }
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="button-delete-student"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <CreditCard className="w-4 h-4" />
            <span>Rate</span>
          </div>
          <p className="text-lg font-semibold">{student.unitRate} {student.unitCurrency}/hr</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Clock className="w-4 h-4" />
            <span>Duration</span>
          </div>
          <p className="text-lg font-semibold">{student.lessonDuration} min</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Calendar className="w-4 h-4" />
            <span>Total Lessons</span>
          </div>
          <p className="text-lg font-semibold">{lessons.length}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <span>Debt</span>
          </div>
          <p className={`text-lg font-semibold ${(debt?.totalDebt || 0) > 0 ? "text-amber-600 dark:text-amber-400" : ""}`}>
            {(debt?.totalDebt || 0).toFixed(2)} {debt?.currency || student.unitCurrency}
          </p>
        </Card>
      </div>

      {student.lessonType === "offline" && student.address && (
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <MapPin className="w-4 h-4" />
            <span>Lesson Address</span>
          </div>
          <p className="text-sm font-medium">{student.address}</p>
        </Card>
      )}

      {scheduleRules.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-medium mb-3">Schedule Template</h3>
          <div className="flex flex-wrap gap-2">
            {scheduleRules.map((rule, i) => (
              <Badge key={i} variant="outline" className="py-1.5 px-3">
                {DAYS[rule.dayOfWeek]} {rule.startTime} ({rule.duration}min, {rule.recurrenceType})
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold">Lessons</h2>
          <div className="flex gap-2">
            <LessonFormDialog
              open={showAddLesson}
              onOpenChange={setShowAddLesson}
              studentId={student.id}
              student={student}
              trigger={
                <Button className="h-10 px-4 text-sm" data-testid="button-add-lesson">
                  <Plus className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Add Lesson</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              }
            />
            <ScheduleEventDialog
              open={showScheduleEvent}
              onOpenChange={setShowScheduleEvent}
              students={[student]}
              preselectedStudentId={student.id}
              trigger={
                <Button variant="outline" className="h-10 px-4 text-sm" data-testid="button-schedule-event">
                  <Calendar className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Schedule new event</span>
                  <span className="sm:hidden">Event</span>
                </Button>
              }
            />
          </div>
        </div>

        <Tabs value={lessonTab} onValueChange={(v) => setLessonTab(v as "unpaid" | "paid" | "all")}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <TabsList className="w-full sm:w-auto" data-testid="tabs-lessons">
              <TabsTrigger value="unpaid">Unpaid ({unpaidLessons.length})</TabsTrigger>
              <TabsTrigger value="paid">Paid ({paidLessonsList.length})</TabsTrigger>
              <TabsTrigger value="all">All ({lessons.length})</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              {lessonTab === "unpaid" && selectedLessonIds.size > 0 && (
                <Button
                  size="sm"
                  onClick={() =>
                    bulkStatusMutation.mutate({
                      lessonIds: [...selectedLessonIds],
                      status: "paid",
                    })
                  }
                  disabled={bulkStatusMutation.isPending}
                  data-testid="button-bulk-mark-paid"
                >
                  Mark {selectedLessonIds.size} as paid
                </Button>
              )}
              {lessonTab === "paid" && selectedLessonIds.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    bulkStatusMutation.mutate({
                      lessonIds: [...selectedLessonIds],
                      status: "pending",
                    })
                  }
                  disabled={bulkStatusMutation.isPending}
                  data-testid="button-bulk-mark-unpaid"
                >
                  Mark {selectedLessonIds.size} as unpaid
                </Button>
              )}
              {selectedLessonIds.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowBulkLessonDeleteConfirm(true)}
                  disabled={bulkDeleteLessonsMutation.isPending}
                  data-testid="button-bulk-delete-lessons"
                >
                  Delete {selectedLessonIds.size}
                </Button>
              )}
            </div>
          </div>

          {(["unpaid", "paid", "all"] as const).map((tab) => {
            const tabLessons =
              tab === "all" ? lessons : tab === "unpaid" ? unpaidLessons : paidLessonsList;
            const showCheck = true;
            return (
              <TabsContent key={tab} value={tab}>
                {tabLessons.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground text-sm">
                    <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p>No {tab === "all" ? "" : tab} lessons</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tabLessons.map((lesson) => (
                      <Card key={lesson.id} className="p-4" data-testid={`lesson-${lesson.id}`}>
                        <div className="flex items-center gap-3">
                          {showCheck && (
                            <Checkbox
                              checked={selectedLessonIds.has(lesson.id)}
                              onCheckedChange={() => toggleLessonSelect(lesson.id)}
                              data-testid={`checkbox-lesson-${lesson.id}`}
                            />
                          )}
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${lesson.paymentStatus === "paid" ? "bg-green-500" : "bg-amber-500"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium">{formatDate(lesson.scheduledDate)}</span>
                              <span className="text-sm text-muted-foreground">{formatTime(lesson.scheduledDate)}</span>
                              <span className="text-xs text-muted-foreground">({lesson.duration}min)</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-muted-foreground">
                                {lesson.unitRate} {lesson.unitCurrency} · {lesson.lessonType}
                              </span>
                              {lesson.address && (
                                <span className="text-xs text-muted-foreground truncate">· {lesson.address}</span>
                              )}
                            </div>
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={lesson.paymentStatus === "paid" ? "outline" : "default"}
                                  size="sm"
                                  onClick={() =>
                                    toggleStatusMutation.mutate({
                                      lessonId: lesson.id,
                                      status: lesson.paymentStatus === "paid" ? "pending" : "paid",
                                    })
                                  }
                                  data-testid={`button-toggle-status-${lesson.id}`}
                                >
                                  {lesson.paymentStatus === "paid" ? (
                                    <>
                                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                                      Paid
                                    </>
                                  ) : (
                                    <>
                                      <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
                                      Mark Paid
                                    </>
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {lesson.paymentStatus === "paid" ? "Mark as Unpaid" : "Mark as Paid"}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setLessonPendingDelete(lesson)}
                            data-testid={`button-delete-lesson-${lesson.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {debt && debt.totalDebt > 0 && (
        <Card className="p-5 border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-900/10" data-testid="card-debt-summary">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            Outstanding Debt Summary
          </h3>
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mb-3">
            {debt.totalDebt.toFixed(2)} {debt.currency}
          </p>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Unpaid lessons:</p>
            {debt.unpaidLessons.map(l => (
              <p key={l.id} className="text-sm">
                {formatDate(l.scheduledDate)} - {(parseFloat(l.unitRate) * l.duration / 60).toFixed(2)} {l.unitCurrency}
              </p>
            ))}
          </div>
        </Card>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Remove {student?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {canDeleteInfo && !canDeleteInfo.canDelete ? (
                <span className="text-destructive font-medium">
                  This student has {canDeleteInfo.unpaidPastLessons} unpaid past lesson{canDeleteInfo.unpaidPastLessons > 1 ? 's' : ''}. Mark them as paid before removing.
                </span>
              ) : (
                "Choose how to remove this student."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 pt-1 pb-2">
            <button
              className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card text-left transition-colors disabled:opacity-50 disabled:pointer-events-none"
              disabled={canDeleteInfo ? !canDeleteInfo.canDelete : false}
              onClick={() => handleDeleteChoice("archive")}
              data-testid="button-archive-student"
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0 mt-0.5">
                <Archive className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <div className="font-medium text-sm">Archive</div>
                <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Removes schedule and future lessons. Keeps lesson history. Can be restored later.</div>
              </div>
            </button>
            <button
              className="flex items-start gap-3 p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-left transition-colors disabled:opacity-50 disabled:pointer-events-none"
              disabled={canDeleteInfo ? !canDeleteInfo.canDelete : false}
              onClick={() => handleDeleteChoice("permanent")}
              data-testid="button-permanent-delete-student"
            >
              <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center shrink-0 mt-0.5">
                <Trash2 className="w-4 h-4 text-destructive" />
              </div>
              <div>
                <div className="font-medium text-sm text-destructive">Permanently Delete</div>
                <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Removes student and all unpaid lessons. Paid lessons are kept for records. Cannot be undone.</div>
              </div>
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={deleteMode === "archive" ? "Archive Student?" : "Permanently Delete Student?"}
        description={
          deleteMode === "archive"
            ? `${student?.name} will be archived. Their schedule and future lessons will be removed, but lesson history will be preserved. You can restore them later.`
            : `${student?.name} will be permanently deleted along with all unpaid lessons and Google Calendar events. Paid lessons will be kept for your records. This cannot be undone.`
        }
        confirmLabel={deleteMode === "archive" ? "Archive" : "Delete Forever"}
        variant="destructive"
        onConfirm={handleConfirmDelete}
        disabled={archiveMutation.isPending || deleteMutation.isPending}
      />
      <ConfirmDialog
        open={!!lessonPendingDelete}
        onOpenChange={(open) => !open && setLessonPendingDelete(null)}
        title="Delete lesson?"
        description="This lesson will be permanently removed from your schedule."
        confirmLabel="Delete lesson"
        variant="destructive"
        onConfirm={() => lessonPendingDelete && queueLessonDelete(lessonPendingDelete.id)}
        disabled={deleteLessonMutation.isPending}
      />
      <ConfirmDialog
        open={showBulkLessonDeleteConfirm}
        onOpenChange={setShowBulkLessonDeleteConfirm}
        title={`Delete ${selectedLessonIds.size} selected lesson${selectedLessonIds.size !== 1 ? "s" : ""}?`}
        description="Selected lessons will be permanently removed from your schedule."
        confirmLabel="Delete selected"
        variant="destructive"
        onConfirm={() => queueBulkLessonDelete([...selectedLessonIds])}
        disabled={bulkDeleteLessonsMutation.isPending}
      />
      <UndoDeleteBanner
        open={!!pendingDeleteUi}
        message={pendingDeleteUi?.message || ""}
        deadline={pendingDeleteUi?.deadline || 0}
        durationMs={pendingDeleteUi?.durationMs || 10_000}
        onUndo={cancelPendingDelete}
      />
    </div>
  );
}
